import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { analyticsConfigured, getConsent, setConsent } from "../analytics";

/**
 * A lightweight cookie/consent banner. Shows only when analytics is actually
 * configured AND the visitor hasn't chosen yet. No tags load until "Allow".
 */
const ConsentBanner: React.FC = () => {
  const [choice, setChoice] = useState<"granted" | "denied" | null>(getConsent());
  const show = analyticsConfigured() && choice === null;

  const decide = (granted: boolean) => {
    setConsent(granted);
    setChoice(granted ? "granted" : "denied");
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          role="dialog"
          aria-label="Cookie consent"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-2xl rounded-2xl border border-primary-100 bg-white/95 p-4 shadow-warm-lg backdrop-blur-sm sm:flex sm:items-center sm:gap-4"
        >
          <p className="text-sm text-ink/70">
            We use cookies to understand how Kinoes is used and to measure our ads. You can decline without
            losing any features. See our{" "}
            <Link to="/privacy" className="underline hover:text-primary-600">
              Privacy Policy
            </Link>
            .
          </p>
          <div className="mt-3 flex shrink-0 gap-2 sm:mt-0">
            <button
              onClick={() => decide(false)}
              className="flex-1 rounded-xl border border-primary-200 px-4 py-2 text-sm font-medium text-ink/70 hover:border-primary-300 sm:flex-none"
            >
              Decline
            </button>
            <button
              onClick={() => decide(true)}
              className="flex-1 rounded-xl bg-primary-500 px-4 py-2 text-sm font-medium text-white hover:bg-primary-600 sm:flex-none"
            >
              Allow
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConsentBanner;
